import createActions from '../config/actions/createActions';
import { action } from '@play-co/replicant';
import { DaycareId, daycareOptions } from './daycare.ruleset';
import { isInDaycare } from './daycare.getters';
import * as Inventory from '../inventory';
import * as Fate from '../fate';
import * as Pet from '../pet';
import { sendAnalyticsEvents } from '../analytics';

export default createActions({
  startDaycare: action((state, id: DaycareId, api) => {
    // Consume points
    const now = api.date.now();
    const { duration, price } = daycareOptions[id];
    Inventory.modifiers.consumePoints(
      state,
      price,
      { feature: 'daycare', $subFeature: id },
      api,
    );

    const { getCurrentEvolution, getAttributeValue } = Pet.getters;
    const { getTimeUntilAwake, getAwakeDuration } = Fate.getters;

    let expiresAt = now + duration;

    // Add any time that the pet was asleep to the daycare expiration timestamp
    const awakeDuration = getAwakeDuration(state, now, expiresAt);
    const asleepDuration = duration - awakeDuration;
    expiresAt += getTimeUntilAwake(state, expiresAt) + asleepDuration;

    // Enable daycare status effect
    Pet.modifiers.setStatusEffect(state, 'daycare', true, now, expiresAt);

    // Add daycare duration to evolution timestamp
    if (!Fate.getters.hasPermanentStatusEffect(state, 'evolving', now)) {
      const { timestamp, stage } = getCurrentEvolution(state)!;
      const nextEvolutionTime =
        timestamp + Pet.getters.getTimeToEvolve(state, stage);
      Fate.modifiers.resolveStatusEffect(
        state,
        'evolving',
        true,
        nextEvolutionTime + duration,
      );
    }

    // Sync current attribute values to daycare expiration timestamp
    Pet.ruleset.petAttributes.forEach((attribute) => {
      const currentValue = getAttributeValue(state, attribute, now);
      Pet.modifiers.setAttribute(state, attribute, currentValue, expiresAt);
    });

    // Update fate
    Fate.modifiers.update(state, now);

    // Schedule chatbot message when daycare expires
    api.scheduledActions.schedule.daycareFinished({
      args: { type: id },
      notificationId: 'daycareFinished',
      delayInMS: expiresAt - now,
    });

    // Send analytics
    sendAnalyticsEvents(api, [
      {
        eventType: 'DaycareCheckIn',
        eventProperties: {
          item_id: id,
        },
      },
    ]);
  }),

  exitDaycare: action((state, id: DaycareId, api) => {
    const now = api.date.now();
    if (!isInDaycare(state, now)) {
      return;
    }

    // Calculate how long pet was in daycare
    const { getStatusEffectStartTime, getAwakeDuration } = Fate.getters;
    const daycareStartTime = getStatusEffectStartTime(state, 'daycare', now);
    const timeInDaycare = getAwakeDuration(state, daycareStartTime, now);
    const minutesInDaycare = Math.floor(timeInDaycare / 60_000);

    // Sync pet stats and fate to current time
    Pet.modifiers.setStatusEffect(state, 'daycare', false, now);
    Pet.modifiers.syncAttributes(state, now);
    Fate.modifiers.update(state, now);

    // Unschedule pending chatbot message
    api.scheduledActions.unschedule('daycareFinished');

    // Send analytics
    sendAnalyticsEvents(api, [
      {
        eventType: 'DaycareCheckOut',
        eventProperties: {
          item_id: id,
          earlyCheckout: true,
          minutesInDaycare,
        },
      },
    ]);
  }),
});
